import { useEffect, useState } from 'react';
import { RoomContext, ROOM_CONTEXTS } from '../types/user';
import { useRooms } from '../context/RoomContext';
import { useUser } from '../context/UserContext';
import { sceneTemplateService, SceneTemplateOption } from '../services/api/sceneTemplateService';
import './CreateRoomModal.css';

interface CreateRoomModalProps {
  onClose: () => void;
  onCreated?: () => void;
  /** Preselect a world in the gallery (e.g. opened from the Explore surface). */
  initialSceneTemplateId?: string;
}

// Fallback glyph per catalog category when a template has no thumbnail (P4).
const WORLD_CATEGORY_GLYPH: Record<string, string> = {
  games: '🎮',
  lobbies: '🛋️',
  interests: '🎯',
  dating: '💘',
};

export function CreateRoomModal({ onClose, onCreated, initialSceneTemplateId = '' }: CreateRoomModalProps) {
  const { createRoom, isConnected } = useRooms();
  const { user } = useUser();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    context: 'casual-hangout' as RoomContext,
    minAge: 18,
    maxAge: 45,
    maxPlayers: 4,
    isPrivate: false,
    password: '',
    tags: '',
    sceneTemplateId: initialSceneTemplateId, // '' = server default world (the bar)
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sceneOptions, setSceneOptions] = useState<SceneTemplateOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    sceneTemplateService
      .getAvailableScenes()
      .then((scenes) => {
        if (!cancelled) setSceneOptions(scenes);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isConnected) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await createRoom({
        name: formData.name,
        description: formData.description,
        context: formData.context,
        hostId: user.id,
        hostName: user.displayName,
        minAge: formData.minAge,
        maxAge: formData.maxAge,
        maxPlayers: formData.maxPlayers,
        isPrivate: formData.isPrivate,
        hasPassword: formData.isPrivate && !!formData.password,
        requiresApproval: formData.isPrivate,
        code: '', // Generated by server
        password: formData.isPrivate ? formData.password : undefined,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        sceneTemplateId: formData.sceneTemplateId || undefined,
      });

      onCreated?.();
      onClose();
    } catch (e) {
      console.error('Failed to create room:', e);
      setError('Failed to create room. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = formData.name.length >= 3 && formData.description.length >= 10 && isConnected;

  return (
    <div className="modal-overlay create-room-modal" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create a Room</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {!isConnected && (
          <div className="error-banner">
            You must be connected to the server to create a room.
          </div>
        )}

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Room Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Give your room a catchy name"
              maxLength={50}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="What's this room about?"
              rows={3}
              maxLength={200}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Room Type</label>
            <div className="context-grid">
              {ROOM_CONTEXTS.map(ctx => (
                <button
                  key={ctx.value}
                  type="button"
                  className={`context-option ${formData.context === ctx.value ? 'selected' : ''}`}
                  onClick={() => handleChange('context', ctx.value)}
                  disabled={isSubmitting}
                >
                  <span className="context-label">{ctx.label}</span>
                  <span className="context-desc">{ctx.description}</span>
                </button>
              ))}
            </div>
          </div>

          {sceneOptions.length > 0 && (
            <div className="form-group">
              <label>Pick a world</label>
              <div className="world-gallery" role="radiogroup" aria-label="World template">
                {/* Default: Classic Bar (no template). */}
                <button
                  type="button"
                  role="radio"
                  aria-checked={formData.sceneTemplateId === ''}
                  className={`world-card ${formData.sceneTemplateId === '' ? 'selected' : ''}`}
                  onClick={() => handleChange('sceneTemplateId', '')}
                  disabled={isSubmitting}
                >
                  <span className="world-card-thumb world-card-thumb--fallback" data-cat="lobbies" aria-hidden="true">🍸</span>
                  <span className="world-card-body">
                    <span className="world-card-name">Classic Bar</span>
                    <span className="world-card-blurb">The default hangout — always open.</span>
                  </span>
                </button>

                {sceneOptions.map((scene) => (
                  <button
                    key={scene.id}
                    type="button"
                    role="radio"
                    aria-checked={formData.sceneTemplateId === scene.id}
                    className={`world-card ${formData.sceneTemplateId === scene.id ? 'selected' : ''}`}
                    onClick={() => handleChange('sceneTemplateId', scene.id)}
                    disabled={isSubmitting}
                  >
                    {scene.thumbnailUrl ? (
                      <img className="world-card-thumb" src={scene.thumbnailUrl} alt="" />
                    ) : (
                      <span
                        className="world-card-thumb world-card-thumb--fallback"
                        data-cat={scene.category ?? 'lobbies'}
                        aria-hidden="true"
                      >
                        {WORLD_CATEGORY_GLYPH[scene.category ?? 'lobbies'] ?? '🌐'}
                      </span>
                    )}
                    <span className="world-card-body">
                      <span className="world-card-name">
                        {scene.name}
                        {scene.category && (
                          <span className={`world-card-chip cat-${scene.category}`}>{scene.category}</span>
                        )}
                      </span>
                      <span className="world-card-blurb">
                        {scene.blurb || scene.description || 'An authored world.'}
                      </span>
                      {(scene.minPlayers ?? 1) > 1 && (
                        <span className="world-card-players">👥 {scene.minPlayers}+ players</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Age Range</label>
              <div className="age-inputs">
                <input
                  type="number"
                  value={formData.minAge}
                  onChange={(e) => handleChange('minAge', parseInt(e.target.value) || 18)}
                  min={18}
                  max={99}
                  disabled={isSubmitting}
                />
                <span>to</span>
                <input
                  type="number"
                  value={formData.maxAge}
                  onChange={(e) => handleChange('maxAge', parseInt(e.target.value) || 99)}
                  min={18}
                  max={99}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Max Players</label>
              <select
                value={formData.maxPlayers}
                onChange={(e) => handleChange('maxPlayers', parseInt(e.target.value))}
                disabled={isSubmitting}
              >
                <option value={2}>2 Players</option>
                <option value={4}>4 Players</option>
                <option value={6}>6 Players</option>
                <option value={8}>8 Players</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="e.g., friendly, casual, lgbtq-friendly"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => handleChange('isPrivate', e.target.checked)}
                disabled={isSubmitting}
              />
              Make this room private
            </label>
          </div>

          {formData.isPrivate && (
            <div className="form-group">
              <label>Room Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Set a password for your room"
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!isValid || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
